import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import HelpIcon from './help/HelpIcon';
import { useUserRole } from '../hooks/useUserRole';
import {
  getInventorySettings,
  updateInventorySettings,
  updateRestaurantInventorySettings,
} from '../services/api';
import {
  toSettingsForm,
  buildOrgSettingsChanges,
  buildLocalSettingsChange,
  isSettingsDirty,
  validateSettingsForm,
  DEDUCTION_TRIGGERS,
  COSTING_METHODS,
  ALERT_CHANNELS,
  ALERT_ROLES,
} from '../utils/inventorySettings';
import { inventoryErrorMessage } from '../utils/inventoryErrors';
import './InventorySettingsSection.css';

/**
 * Políticas de inventario de la organización (RES-26): se definen una vez y todos los locales las
 * heredan. La única que cada local puede tener distinta es a quién avisa el stock bajo.
 *
 * Solo se muestran las políticas que hoy cambian el comportamiento; las que quedan para épicas
 * siguientes se guardan por API pero no se ofrecen acá.
 * @param {{onDirtyChange?: Function}} props - Avisa al contenedor si hay cambios sin guardar
 * @returns {JSX.Element|null} Sección de configuración
 */
function InventorySettingsSection({ onDirtyChange }) {
  const { t } = useTranslation();
  const { hasRole } = useUserRole();

  const [view, setView] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // RES-28: qué puede hacer este usuario lo decide el backend (rol + membresía en la
  // organización + candado del catálogo); el rol local es solo el respaldo si el backend es viejo.
  const fallback = hasRole(['OWNER', 'ADMIN']);
  const can = view?.can;
  const canEdit = can ? can.settingsWrite : fallback;
  const canEditLocalAlert = can ? can.settingsLocalWrite : fallback;
  // La alerta se guarda en la organización mientras el local la herede
  const canEditAlert = form?.alertInherited ? canEdit : canEditLocalAlert;

  const dirty = Boolean(view && form && isSettingsDirty(form, view));

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getInventorySettings();
      setView(data);
      setForm(toSettingsForm(data));
      setUnavailable(false);
    } catch (err) {
      // 403 del gate de plan (inventario es Profesional+): la sección simplemente no aplica
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (unavailable) return null;

  const setField = (name, value) => {
    setSuccess('');
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleIn = (name, value) => {
    setSuccess('');
    setForm((prev) => {
      const list = prev[name];
      return {
        ...prev,
        [name]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
      };
    });
  };

  const handleSave = async () => {
    const check = validateSettingsForm(form);
    if (!check.ok) {
      setError(t(`settings.inventoryPolicies.invalid.${check.field}`));
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const orgChanges = buildOrgSettingsChanges(form, view);
      const localChange = buildLocalSettingsChange(form, view);
      let latest = view;
      if (orgChanges) latest = await updateInventorySettings(orgChanges);
      if (localChange) latest = await updateRestaurantInventorySettings(localChange);
      setView(latest);
      setForm(toSettingsForm(latest));
      setSuccess(t('settings.inventoryPolicies.saveSuccess'));
    } catch (err) {
      console.error('Error saving inventory settings:', err.response?.data || err);
      setError(inventoryErrorMessage(t, err, 'settings.inventoryPolicies.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const options = (view && view.options) || {};
  const triggers = options.deductionTrigger || DEDUCTION_TRIGGERS;
  const methods = options.costingMethod || COSTING_METHODS;
  const channels = options.lowStockAlert?.channels || ALERT_CHANNELS;
  const roles = options.lowStockAlert?.roles || ALERT_ROLES;

  /** Fila con título, explicación y control. */
  const Row = ({ name, children }) => (
    <div className="setting-item inventory-policy">
      <div className="setting-info">
        <h3>{t(`settings.inventoryPolicies.${name}.label`)}</h3>
        <p className="setting-description">{t(`settings.inventoryPolicies.${name}.help`)}</p>
      </div>
      <div className="setting-action">{children}</div>
    </div>
  );

  const Switch = ({ name }) => (
    <label className="switch">
      <input
        type="checkbox"
        checked={form[name]}
        disabled={!canEdit}
        aria-label={t(`settings.inventoryPolicies.${name}.label`)}
        onChange={() => setField(name, !form[name])}
      />
      <span className="slider round"></span>
    </label>
  );

  return (
    <div className="card settings-section settings-section--org-scope">
      <div className="section-header">
        <Icon name="inventory_2" />
        <h2>
          {t('settings.inventoryPolicies.title')} <HelpIcon topicKey="inventory.policies" />
        </h2>
        <span className="scope-badge scope-badge--org">
          <Icon name="account_tree" size="small" />
          {t('settings.scopeBadge.organization')}
        </span>
        {dirty && <span className="dirty-badge">{t('common.unsavedChangesTitle')}</span>}
      </div>

      <div className="section-content">
        {loading || !form ? (
          <p className="setting-description">{t('common.loading')}</p>
        ) : (
          <>
            {success && (
              <div className="alert alert-success">
                <Icon name="check_circle" />
                <span>{success}</span>
              </div>
            )}
            {error && (
              <div className="alert alert-error">
                <Icon name="error" />
                <span>{error}</span>
              </div>
            )}
            {!canEdit && (
              <p className="setting-description inventory-policy-readonly">
                <Icon name="info" size="small" />
                {t('settings.inventoryPolicies.definedByOrg')}
              </p>
            )}

            <Row name="deductionTrigger">
              <select
                id="inventory-deduction-trigger"
                className="form-input"
                value={form.deductionTrigger}
                disabled={!canEdit}
                onChange={(e) => setField('deductionTrigger', e.target.value)}
              >
                {triggers.map((value) => (
                  <option key={value} value={value}>
                    {t(`settings.inventoryPolicies.deductionTrigger.options.${value}`)}
                  </option>
                ))}
              </select>
            </Row>

            <Row name="restockOnCancel">
              <Switch name="restockOnCancel" />
            </Row>

            <Row name="allowNegativeStock">
              <Switch name="allowNegativeStock" />
            </Row>

            <Row name="costingMethod">
              <select
                id="inventory-costing-method"
                className="form-input"
                value={form.costingMethod}
                disabled={!canEdit}
                onChange={(e) => setField('costingMethod', e.target.value)}
              >
                {methods.map((value) => (
                  <option key={value} value={value}>
                    {t(`settings.inventoryPolicies.costingMethod.options.${value}`)}
                  </option>
                ))}
              </select>
            </Row>

            <Row name="expirationWarningDays">
              <input
                id="inventory-expiration-days"
                type="number"
                className="form-input inventory-days-input"
                min="0"
                max="60"
                value={form.expirationWarningDays}
                disabled={!canEdit}
                onChange={(e) => setField('expirationWarningDays', e.target.value)}
              />
            </Row>

            <div className="inventory-alert-block">
              <h3>{t('settings.inventoryPolicies.lowStockAlert.label')}</h3>
              <p className="setting-description">
                {t('settings.inventoryPolicies.lowStockAlert.help')}
              </p>

              <label className="inventory-inherit-toggle">
                <input
                  type="checkbox"
                  checked={form.alertInherited}
                  disabled={!canEditLocalAlert}
                  onChange={() => setField('alertInherited', !form.alertInherited)}
                />
                <span>{t('settings.inventoryPolicies.lowStockAlert.useOrg')}</span>
              </label>

              <fieldset className="inventory-alert-group" disabled={!canEditAlert}>
                <legend>{t('settings.inventoryPolicies.lowStockAlert.channels')}</legend>
                {channels.map((value) => (
                  <label key={value} className="inventory-check">
                    <input
                      type="checkbox"
                      checked={form.alertChannels.includes(value)}
                      onChange={() => toggleIn('alertChannels', value)}
                    />
                    <span>
                      {t(`settings.inventoryPolicies.lowStockAlert.channelOptions.${value}`)}
                    </span>
                  </label>
                ))}
              </fieldset>

              <fieldset className="inventory-alert-group" disabled={!canEditAlert}>
                <legend>{t('settings.inventoryPolicies.lowStockAlert.roles')}</legend>
                {roles.map((value) => (
                  <label key={value} className="inventory-check">
                    <input
                      type="checkbox"
                      checked={form.alertRoles.includes(value)}
                      onChange={() => toggleIn('alertRoles', value)}
                    />
                    <span>
                      {t(`settings.inventoryPolicies.lowStockAlert.roleOptions.${value}`)}
                    </span>
                  </label>
                ))}
              </fieldset>

              {!form.alertInherited && (
                <p className="setting-description inventory-local-note">
                  <Icon name="store" size="small" />
                  {t('settings.inventoryPolicies.lowStockAlert.localNote')}
                </p>
              )}
            </div>

            {(canEdit || canEditLocalAlert) && (
              <div className="setting-item billing-actions">
                <div className="setting-info" />
                <div className="setting-action">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving || !dirty}
                  >
                    {saving ? (
                      <>
                        <Icon name="hourglass_empty" />
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <Icon name="save" />
                        {t('common.save')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default InventorySettingsSection;
